import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { posts } from '../blog/posts';

const SITE_URL = 'https://wrapalotspecialtyfilmz.com';

const PostCard = ({ post }) => (
  <Link
    to={`/blog/${post.slug}`}
    data-testid={`blog-card-${post.slug}`}
    className="group flex flex-col bg-slate-800/50 rounded-xl overflow-hidden hover:bg-slate-800/70 transition-all duration-300 hover:transform hover:scale-[1.01] hover:shadow-xl"
  >
    <div className="relative h-52 overflow-hidden">
      <img
        src={post.image}
        alt={post.imageAlt}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
    </div>
    <div className="p-6 flex-1 flex flex-col">
      <h3 className="text-xl md:text-2xl font-bold text-white mb-3 leading-tight group-hover:text-cyan-400 transition-colors">
        {post.title}
      </h3>
      <p className="text-gray-400 leading-relaxed text-sm md:text-base mb-5 flex-1">
        {post.excerpt}
      </p>
      <div className="flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            {new Date(post.publishedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
          <span className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            {post.readTime} min read
          </span>
        </div>
        <span className="flex items-center gap-1 text-cyan-400 font-medium group-hover:gap-2 transition-all">
          Read <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </div>
  </Link>
);

const BlogIndex = () => {
  const url = `${SITE_URL}/blog`;

  return (
    <>
      <Helmet>
        <title>Car Wrap Installation Blog — Sacramento, CA | WrapAlot Specialty Filmz</title>
        <meta
          name="description"
          content="Expert guides on car wrap installation, pricing, paint protection film and commercial fleet wraps in Sacramento, CA. Updated regularly by WrapAlot Specialty Filmz."
        />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Car Wrap Installation Blog — Sacramento, CA | WrapAlot Specialty Filmz" />
        <meta property="og:url" content={url} />
        <meta property="og:description" content="Expert guides on car wrap installation, pricing, paint protection film and commercial fleet wraps in Sacramento, CA." />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Blog',
            name: 'WrapAlot Specialty Filmz Blog',
            url,
            description: 'Expert guides on car wrap installation in Sacramento, CA.',
            publisher: { '@id': `${SITE_URL}/#business` },
            blogPost: posts.map((p) => ({
              '@type': 'BlogPosting',
              headline: p.title,
              url: `${SITE_URL}/blog/${p.slug}`,
              datePublished: p.publishedDate,
              dateModified: p.updatedDate,
              image: p.image,
            })),
          })}
        </script>
      </Helmet>

      <section className="bg-slate-900 pt-32 pb-12 md:pt-40 md:pb-16" data-testid="blog-index-hero">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
            Car Wrap Insights for <span className="text-cyan-400">Sacramento</span>
          </h1>
          <p className="text-gray-400 text-lg md:text-xl max-w-3xl mx-auto">
            Practical guides on car wraps, paint protection film, fleet graphics, pricing and care — written by the team installing them every day in Sacramento, CA.
          </p>
        </div>
      </section>

      <section className="bg-slate-900 pb-20 md:pb-28">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {posts.map((post) => (
              <PostCard key={post.slug} post={post} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogIndex;
